import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/app/components/ui/sheet';
import { useDispatchHistory } from '@/hooks/use-dispatch-history';
import type { DispatchBatch, DispatchPacket, DeliveryStatus } from '@/hooks/use-dispatch-history';
import { useReadinessOverrides, OVERRIDE_ACTION_LABELS } from '@/hooks/use-readiness-overrides';
import type { ReadinessOverride } from '@/hooks/use-readiness-overrides';
import { AlertTriangle, CheckCircle2, Clock, Eye, Loader2, Mail, MessageSquare, ShieldAlert, Smartphone, XCircle, XOctagon } from 'lucide-react';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deliveryBadge(deliveryStatus: DeliveryStatus) {
  switch (deliveryStatus) {
    case 'sent':
    case 'delivered':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
          <CheckCircle2 className="h-3 w-3" />
          {deliveryStatus === 'delivered' ? 'Delivered' : 'Sent'}
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    case 'no_channel':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
          <AlertTriangle className="h-3 w-3" />
          Not Reached
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          <Clock className="h-3 w-3" />
          Queued
        </span>
      );
  }
}

function statusBadge(status: DispatchPacket['status']) {
  switch (status) {
    case 'acknowledged':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
          <CheckCircle2 className="h-3 w-3" />
          Acknowledged
        </span>
      );
    case 'viewed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
          <Eye className="h-3 w-3" />
          Viewed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          <Clock className="h-3 w-3" />
          Sent
        </span>
      );
  }
}

function channelIcon(sentVia: string) {
  switch (sentVia) {
    case 'sms':
      return <Smartphone className="h-3.5 w-3.5 text-gray-400" />;
    case 'app_only':
      return <MessageSquare className="h-3.5 w-3.5 text-gray-400" />;
    default:
      return <Mail className="h-3.5 w-3.5 text-gray-400" />;
  }
}

function formatTime(iso: string, timeZone: string) {
  if (!iso) return '';
  return formatDateTimeInTimeZone(iso, timeZone, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Batch card ──────────────────────────────────────────────────────────────

function BatchCard({ batch, timeZone }: { batch: DispatchBatch; timeZone: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/60 rounded-t-lg">
        <span className="text-xs font-medium text-gray-700">
          {formatTime(batch.dispatched_at, timeZone)}
        </span>
        <span className="text-xs text-gray-500">
          {batch.project_count} project{batch.project_count !== 1 ? 's' : ''} · {batch.tech_count} tech{batch.tech_count !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="divide-y divide-gray-100">
        {batch.packets.map((pkt, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2.5 md:py-2">
            {channelIcon(pkt.sent_via)}
            <div className="flex-1 min-w-0">
              <a
                href={`/crew/techs/${pkt.tech_id}`}
                className="block text-sm font-medium text-gray-900 truncate hover:text-blue-600 hover:underline"
              >
                {pkt.tech_name}
              </a>
              <p className="text-xs text-gray-500 truncate">{pkt.project_name}</p>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              {deliveryBadge(pkt.delivery_status)}
              {pkt.status !== 'sent' && statusBadge(pkt.status)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Override card ────────────────────────────────────────────────────────────

function overrideIcon(action: ReadinessOverride['action']) {
  if (action === 'dispatched_with_blockers') {
    return <XOctagon className="h-4 w-4 text-red-500 flex-shrink-0" />;
  }
  if (action === 'dispatched_with_warnings') {
    return <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
  }
  return <ShieldAlert className="h-4 w-4 text-blue-500 flex-shrink-0" />;
}

function OverrideCard({ override, timeZone }: { override: ReadinessOverride; timeZone: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid="override-card">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60 rounded-t-lg">
        {overrideIcon(override.action)}
        <span className="text-xs font-medium text-gray-700 flex-1">
          {OVERRIDE_ACTION_LABELS[override.action]}
        </span>
        <span className="text-xs text-gray-500">
          {formatTime(override.created_at, timeZone)}
        </span>
      </div>
      <div className="px-3 py-2.5 space-y-1.5">
        <p className="text-sm text-gray-800">{override.reason}</p>
        <p className="text-xs text-gray-500">
          by {override.actor_name}
        </p>
      </div>
    </div>
  );
}

// ─── Main drawer ─────────────────────────────────────────────────────────────

interface DispatchHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  timeZone?: string;
}

export function DispatchHistoryDrawer({ open, onClose, timeZone = 'UTC' }: DispatchHistoryDrawerProps) {
  const { data, isLoading, error } = useDispatchHistory(open);
  const { data: overrides, isLoading: overridesLoading } = useReadinessOverrides(undefined, open);

  const loading = isLoading || overridesLoading;
  const hasOverrides = overrides && overrides.length > 0;
  const hasBatches = data && data.batches.length > 0;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg">
        <SheetHeader>
          <SheetTitle>Dispatch History</SheetTitle>
          <SheetDescription>
            Recent dispatch events, delivery status, and readiness override decisions.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading history...</span>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Failed to load dispatch history.
            </div>
          )}

          {!loading && !hasBatches && !hasOverrides && (
            <div className="text-center py-12">
              <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No dispatches yet.</p>
            </div>
          )}

          {hasOverrides && (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">
                Readiness Overrides
              </p>
              {overrides.map((override) => (
                <OverrideCard key={override.id} override={override} timeZone={timeZone} />
              ))}
            </>
          )}

          {hasBatches && (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">
                Dispatch Batches
              </p>
              {data.batches.map((batch) => (
                <BatchCard key={batch.batch_id} batch={batch} timeZone={timeZone} />
              ))}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
